/**
 * Pure helpers for the adjustable schedule layout: per-row heights and the
 * label-column width. Shared by the schedule creator (CrewSchedule) and the
 * read-only timeline (producer Schedule page + public client-share page).
 *
 * Both preferences are per-browser UI state (localStorage via the repo's
 * safeStorage wrapper), keyed per briefing so productions don't inherit each
 * other's layout. Row keys are human-stable (stage label / crew name) so a
 * row keeps its height across festival days and across the creator and
 * read-only views — and so the unauthenticated client-share page can
 * persist them too.
 */

import { safeLocalStorage, type StorageLike } from "@/lib/safeStorage";
import {
  clampDimension,
  clampToRange,
  loadDimension,
  saveDimension,
  type ScheduleDimension,
} from "@/lib/scheduleDimension";
import { stageKeyFor } from "@/lib/scheduleStages";

export const MIN_ROW_HEIGHT = 40;
export const MAX_ROW_HEIGHT = 400;

export const MIN_LABEL_WIDTH = 80;
export const MAX_LABEL_WIDTH = 400;

export type RowHeightMap = Record<string, number>;

/**
 * The label column is per-view: the creator (120px column) and the read-only
 * timeline (180px column) have different defaults and layouts, so narrowing
 * one must not silently narrow the other — hence the scope segment.
 */
export const LABEL_WIDTH_DIMENSION: ScheduleDimension = {
  keyPrefix: "scheduleLabelWidth",
  min: MIN_LABEL_WIDTH,
  max: MAX_LABEL_WIDTH,
};

export function clampRowHeight(height: number): number {
  return clampToRange(height, MIN_ROW_HEIGHT, MAX_ROW_HEIGHT);
}

export function clampLabelWidth(width: number): number {
  return clampDimension(LABEL_WIDTH_DIMENSION, width);
}

/**
 * Row key for a stage lane. Delegates to the shared `stageKeyFor` (the
 * label||name canonical key both display code and data filters agree on) so
 * the creator and read-only views can't silently drift onto different keys
 * for the same stage.
 */
export function stageRowKey(stage: { label?: string | null; name?: string | null }): string {
  return `stage:${stageKeyFor({ name: stage.name ?? "", label: stage.label ?? "" })}`;
}

/**
 * Row key for a crew member's custom lane. Keyed by the crew member's stable
 * id (not name) so two crew with the same display name don't share a height,
 * and a rename doesn't orphan the stored value.
 */
export function customRowKey(crewId: string): string {
  return `custom:${crewId}`;
}

const storageKey = (briefingId: string) => `produce.scheduleRowHeights.${briefingId}`;

export function loadRowHeights(
  briefingId: string,
  storage: StorageLike = safeLocalStorage,
): RowHeightMap {
  if (!briefingId) return {};
  const raw = storage.getItem(storageKey(briefingId));
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
  const heights: RowHeightMap = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      heights[key] = clampRowHeight(value);
    }
  }
  return heights;
}

export function saveRowHeights(
  briefingId: string,
  heights: RowHeightMap,
  storage: StorageLike = safeLocalStorage,
): void {
  if (!briefingId) return;
  storage.setItem(storageKey(briefingId), JSON.stringify(heights));
}

/**
 * The label column (stage / crew names) has one adjustable width per
 * briefing. Returns null when the producer never adjusted it, so each
 * view keeps its own default width.
 */
export function loadLabelWidth(
  briefingId: string,
  scope: string,
  storage: StorageLike = safeLocalStorage,
): number | null {
  return loadDimension(LABEL_WIDTH_DIMENSION, briefingId, scope, storage);
}

export function saveLabelWidth(
  briefingId: string,
  scope: string,
  width: number | null,
  storage: StorageLike = safeLocalStorage,
): void {
  saveDimension(LABEL_WIDTH_DIMENSION, briefingId, scope, width, storage);
}

/**
 * A horizontal band inside a stage row, in row-absolute pixels.
 */
export interface RowBand {
  top: number;
  height: number;
}

/** Height of one duty lane in the stage-duties strip. */
export const DUTY_LANE_HEIGHT = 20;

/**
 * Strip for stage-bound duties ("overzicht stage", "publiek", …) appended
 * BELOW the row's inner block area. The artist area keeps its full height —
 * the row grows by `extraHeight` instead of shrinking — and the strip gets
 * one fixed-height lane per concurrent duty so stacked duties stay readable.
 * `area.top` doubles as the gap above the strip, mirroring the row's own
 * vertical padding. No duties → no strip, no extra height.
 */
export function stageDutyStrip(
  area: RowBand,
  laneCount: number,
): { band: RowBand | null; extraHeight: number; hitTop: number | null } {
  if (laneCount <= 0) return { band: null, extraHeight: 0, hitTop: null };
  const gap = area.top;
  const height = laneCount * DUTY_LANE_HEIGHT;
  return {
    band: { top: area.top + area.height + gap, height },
    extraHeight: gap + height,
    // Drop hit-test boundary: the midpoint of the gap, so a drop is "in the
    // strip" once it's closer to the strip than to the artist area. Derived
    // here so geometry and hit-test can't disagree.
    hitTop: area.top + area.height + gap / 2,
  };
}

/**
 * How many lanes the stage-duties strip occupies for one stage row.
 *
 * The rule depends only on the row's DATA — deliberately never on whether a
 * drag or a clipboard entry happens to be live. A strip that pops into
 * existence when a drag starts changes the row's height, which pushes every
 * row below it (and the very block the producer just grabbed) out from under
 * the pointer: measured at 24px per stage row above it, so ~120px down in a
 * seven-stage festival. Chrome abandons a drag it is still initiating when
 * the source moves like that, and abandons it WITHOUT firing `dragend` — so
 * the editor kept painting every drop zone and swallowed every click until a
 * reload. The editor therefore reserves the empty lane up front: the strip is
 * always there, always the same height, and a drag only changes its styling.
 *
 * Stage rows without artists get no strip — their duties fill the row area
 * itself, so that whole area already is the duties zone.
 */
export function stageDutyStripLanes(params: {
  /** False for an orphan lineup-only row, which can hold no duties at all. */
  hasStageRow: boolean;
  hasArtists: boolean;
  /** Lanes the row's actual duties need (0 when it has none). */
  dutyLanes: number;
  /**
   * Editor only: keep one empty lane so the duty drop target never appears
   * or disappears mid-interaction. The read-only views take no drops and so
   * reserve nothing.
   */
  reserveDropLane?: boolean;
}): number {
  const { hasStageRow, hasArtists, dutyLanes, reserveDropLane = false } = params;
  if (!hasStageRow || !hasArtists) return 0;
  return reserveDropLane ? Math.max(dutyLanes, 1) : dutyLanes;
}

/**
 * Row-relative y below which a drop counts as "on the stage duties" rather
 * than "on an artist", or null when the row can take no duty at all.
 *
 * Lives next to the geometry it reads for the same reason `stageDutyStrip`
 * derives its own `hitTop`: what the producer sees and where a drop lands
 * must come from one rule. A stage row WITHOUT artists has no strip because
 * its duties fill the row area — so on that row the whole area is the duty
 * zone, hence 0.
 */
export function stageDutyDropTop(
  strip: { hitTop: number | null },
  row: { hasStageRow: boolean; hasArtists: boolean },
): number | null {
  if (!row.hasStageRow) return null;
  return row.hasArtists ? strip.hitTop : 0;
}

/**
 * Geometry for one duty block: a fixed-height lane inside the appended strip
 * (1px breathing room between lanes), or — on stages without artists, where
 * no strip is appended — a stacked share of the row area itself. Shared by
 * the creator and read-only views so the lane insets can't drift apart.
 */
export function dutyBlockLayout(
  strip: { band: RowBand | null },
  area: RowBand,
  lane: { laneIndex: number; laneCount: number },
): { top: number; height: number } {
  if (strip.band) {
    return {
      top: strip.band.top + lane.laneIndex * DUTY_LANE_HEIGHT + 1,
      height: DUTY_LANE_HEIGHT - 2,
    };
  }
  return stackedBlockLayout({
    innerTop: area.top,
    innerHeight: area.height,
    totalOverlapping: lane.laneCount,
    overlapIndex: lane.laneIndex,
  });
}

/**
 * Geometry for assignment blocks stacked inside one row. When several
 * assignments overlap in time the row's inner area is subdivided evenly,
 * so a taller row directly buys each stacked block more height.
 */
export function stackedBlockLayout(params: {
  innerTop: number;
  innerHeight: number;
  totalOverlapping: number;
  overlapIndex: number;
}): { top: number; height: number } {
  const { innerTop, innerHeight, totalOverlapping, overlapIndex } = params;
  if (totalOverlapping <= 1) {
    return { top: innerTop, height: innerHeight };
  }
  const height = innerHeight / totalOverlapping;
  return { top: innerTop + overlapIndex * height, height };
}
